import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import uiConfig from "../uiConfig";
import { getMessageForFetchError } from "../shared/utils/apiError";
import { getRepeatLabel } from "../shared/utils/repeatOptions";
import ErrorBanner from "../components/ErrorBanner";
import ConfirmationModal from "../components/ConfirmationModal";
import EntityListBulkToolbar from "../components/entity/EntityListBulkToolbar";
import EventDetailModal from "../components/EventDetailModal";
import EventDescriptionPreview from "../components/EventDescriptionPreview";
import KidHoursTrackingStatus from "../components/KidHoursTrackingStatus";
import "./CloseEventRecord.css";
import { IconCalendar, IconLocation, IconApprove, IconCancel, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import { resolveEventBackgroundImageUrl } from "../shared/utils/eventBackgroundImage";
import { formatPhoneDisplay } from "../shared/utils/phoneFormat";
import { sortRowsByConfig } from "../shared/utils/sortRowsByConfig";
import { buildEventsManagementPath, EVENT_MGMT_TAB_CLOSE_RECORD, EVENT_MGMT_TAB_EVENTS } from "../shared/utils/eventManagementTabs";
import { buildEventCheckInPath } from "../shared/utils/eventCalendarNavigation";
import {
  buildParticipantSortRow,
  getParticipantRegisteredAtCell,
  getParticipantRegTypeCell,
} from "../shared/utils/eventParticipantTableHelpers";
import {
  SCAN_MODAL_TONES,
  scanModalBodyOuterStyle,
  SCAN_MODAL_DETAIL_LABEL_STYLE,
  SCAN_MODAL_DETAIL_NAME_STYLE,
  SCAN_MODAL_DETAIL_META_STYLE,
  scanModalOutcomeBoxStyle,
} from "../shared/utils/eventCheckInModalMessageLayout";
import {
  getParticipantGenderSummaryBucket,
  getParticipantGenderTableLabel,
} from "../shared/utils/genderOptions";
import { fetchCurrentAopProgramNames } from "../shared/api/currentAopByProgram";
import { isEventProgramInCurrentAopForClose } from "../shared/utils/currentAopByProgram";
import { formatKidHours } from "../shared/utils/eventKidHours";

import { apiFetch } from "../shared/api/apiFetch";

/** Stored roster status only — not derived from checkboxes. */
function getServerAttendancePillKey(p) {
  const a = (p.attendance || "").trim();
  if (a === "Attended") return "attended";
  if (a === "Walk-In") return "walkin";
  if (a === "No-Show") return "noshow";
  if (a === "Registered") return "pending";
  if (a === "Waitlisted") return "waitlist";
  if (a === "Cancelled") return "cancelled";
  return "other";
}

function getServerAttendanceSortLabel(p) {
  const k = getServerAttendancePillKey(p);
  if (k === "attended") return "Attended";
  if (k === "walkin") return "Walk-In";
  if (k === "noshow") return "No-Show";
  if (k === "pending") return "Pending";
  if (k === "waitlist") return "Waitlist";
  if (k === "cancelled") return "Cancelled";
  return (p.attendance || "").trim() || "—";
}

function canCloseRecordCheckIn(p) {
  const k = getServerAttendancePillKey(p);
  return k === "pending" || k === "noshow";
}

function canCloseRecordNoShow(p) {
  return getServerAttendancePillKey(p) === "pending";
}

/** Same buckets as Summary by Attendance pills (and the Attendance toolbar filter). */
const CLOSE_RECORD_ATTENDANCE_FILTER_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "attended_checked_in", label: "Checked-in" },
  { value: "no_show", label: "No-Show" },
  { value: "waitlisted", label: "Waitlisted" },
  { value: "cancelled", label: "Cancelled" },
];

/** Final attendance to send on close — from stored roster, not from table selection. */
function getAttendanceForClosePayload(p) {
  const a = (p.attendance || "").trim();
  if (a === "Attended") return "Attended";
  if (a === "Walk-In") return "Walk-In";
  if (a === "No-Show") return "No-Show";
  if (a === "Registered") return "No-Show";
  if (a === "Waitlisted" || a === "Cancelled") return null;
  return "No-Show";
}

/** Styled modal body aligned with Event QR Check-in (waitlist / scan) dialogs. */
function PendingAttendanceModalMessage({ pendingCount, eventName, eventDateTime, continueActionLabel = "Next" }) {
  const tone = SCAN_MODAL_TONES.waitlistPrompt;
  const showEvent = eventName && String(eventName).trim();
  return (
    <div style={scanModalBodyOuterStyle(tone)}>
      {showEvent ? (
        <>
          <div style={SCAN_MODAL_DETAIL_LABEL_STYLE}>Event</div>
          <p style={{ ...SCAN_MODAL_DETAIL_NAME_STYLE, fontWeight: 500 }}>{eventName}</p>
          {eventDateTime ? <p style={SCAN_MODAL_DETAIL_META_STYLE}>{eventDateTime}</p> : null}
        </>
      ) : null}
      <div
        style={{
          ...scanModalOutcomeBoxStyle(tone, 600),
          marginTop: showEvent ? 12 : 0,
        }}
      >
        <p style={{ margin: "0 0 8px 0" }}>
          Pending attendance found ({pendingCount} participant{pendingCount === 1 ? "" : "s"}).
        </p>
        <p style={{ margin: 0, fontWeight: 500 }}>
          Select pending rows and use <strong>Check in</strong> or <strong>No-show</strong> in the bulk actions bar, or
          mark each pending row from the Actions column. When no pending rows remain, click {continueActionLabel} again
          to continue.
        </p>
      </div>
    </div>
  );
}

/**
 * @param {object} [props]
 * @param {object|null} [props.preselectedEvent] - When set with embedded, skip event picker and open review for this event.
 * @param {boolean} [props.embedded] - Hosted inside Event Check-In (or similar); no step-1 picker.
 * @param {(result?: { newEventId?: string }) => void} [props.onClosed] - Called after a successful close in embedded mode.
 */
function CloseEventRecord({ preselectedEvent = null, embedded = false, onClosed = null } = {}) {
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
  const account_id = userInfo.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const [searchParams, setSearchParams] = useSearchParams();

  const [step, setStep] = useState(embedded ? 2 : 1);
  const [programs, setPrograms] = useState([]);
  /** false until getProgram returns — needed so program_id can resolve before AOP membership decides the close path */
  const [programsLoaded, setProgramsLoaded] = useState(false);
  const [programMembers, setProgramMembers] = useState([]);
  const [openEvents, setOpenEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bannerError, setBannerError] = useState("");
  const [alertDialog, setAlertDialog] = useState({ open: false, title: "Message", message: "" });
  const [detailEventId, setDetailEventId] = useState(null);

  // Search filters (step 1)
  const [filterProgramId, setFilterProgramId] = useState("");
  const [filterOrganizerId, setFilterOrganizerId] = useState("");
  const [filterFreeText, setFilterFreeText] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Wizard steps (record flow): 0 = review attendance, 1 = record & retrospective
  const [wizardStep, setWizardStep] = useState(0);

  // Record form
  const [girlsParticipated, setGirlsParticipated] = useState(0);
  const [boysParticipated, setBoysParticipated] = useState(0);
  const [womenParticipated, setWomenParticipated] = useState(0);
  const [menParticipated, setMenParticipated] = useState(0);
  const [anotherParticipated, setAnotherParticipated] = useState(0);
  const [durationHours, setDurationHours] = useState("");
  /** Optional adjustment hours — same semantics as Edit Event `impact_hours_entered`. */
  const [impactHoursEntered, setImpactHoursEntered] = useState("");
  const [whatWentWell, setWhatWentWell] = useState("");
  const [whatCouldImprove, setWhatCouldImprove] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [newEventIdFromSubmit, setNewEventIdFromSubmit] = useState(null);
  const lastAutoFillEventIdRef = useRef(null);

  // Participants for selected event (left = registered, right = attended)
  const [eventParticipants, setEventParticipants] = useState([]);
  const [eventParticipantsLoading, setEventParticipantsLoading] = useState(false);
  /** Table row selection for bulk Check in / No-show — never pre-filled, not used for close payload. */
  const [bulkSelectedIds, setBulkSelectedIds] = useState(() => new Set());
  const [noShowDialog, setNoShowDialog] = useState({ open: false, participantIds: [], message: "" });
  /** Step 1: filter / sort for participant table (aligned with EventQRCheckIn Pending Check-In). */
  const [checkInSearch, setCheckInSearch] = useState("");
  const [checkInSortConfig, setCheckInSortConfig] = useState({ key: null, direction: "asc" });
  /** '' | 'Registered' | 'Walk-In' — from Summary by Reg Type pills only; summaries use full slot list. */
  const [regTypeFilter, setRegTypeFilter] = useState("");
  /** '' | 'male' | 'female' | 'another' | 'unspecified' — from Summary by Gender pills only. */
  const [genderFilter, setGenderFilter] = useState("");
  /** Metric pill filter (summary by attendance); cleared when using reg-type summary pills. */
  const [attendanceMetricFilter, setAttendanceMetricFilter] = useState("");
  /** Participant _id while a single-row Check in / No-show API call is in flight */
  const [closeRecordActionBusyId, setCloseRecordActionBusyId] = useState(null);
  /** null | "checkin" | "noshow" while a bulk attendance update is in flight */
  const [closeRecordBulkBusy, setCloseRecordBulkBusy] = useState(null);
  /** null = not loaded yet (treat as AOP until known); Set of program names in Current AOP By Program */
  const [aopProgramNames, setAopProgramNames] = useState(null);
  const [aopProgramsLoaded, setAopProgramsLoaded] = useState(false);

  const navigate = useNavigate();

  const loadPrograms = useCallback(() => {
    setProgramsLoaded(false);
    apiFetch(serverUrl + "/getProgram", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setPrograms(Array.isArray(data) ? data : []))
      .catch(() => setPrograms([]))
      .finally(() => setProgramsLoaded(true));
  }, [account_id, serverUrl]);

  const loadProgramMembers = useCallback(
    (programId) => {
      if (!programId) {
        setProgramMembers([]);
        return;
      }
      apiFetch(serverUrl + "/getProgramOrganizers", {
        method: "POST",
        body: JSON.stringify({ account_id, program_id: programId }),
      })
        .then((resp) => resp.json())
        .then((data) => setProgramMembers(Array.isArray(data) ? data : []))
        .catch(() => setProgramMembers([]));
    },
    [account_id, serverUrl]
  );

  const searchOpenEvents = useCallback(() => {
    setLoading(true);
    setBannerError("");
    apiFetch(serverUrl + "/getOpenEvents", {
      method: "POST",
      body: JSON.stringify({
        account_id,
        program_id: (filterProgramId || "").trim() || undefined,
        event_organizer_id: (filterOrganizerId || "").trim() || undefined,
        free_text: (filterFreeText || "").trim() || undefined,
        include_private: true,
      }),
    })
      .then((resp) => resp.json())
      .then((data) => setOpenEvents(Array.isArray(data) ? data : []))
      .catch((err) => setBannerError(getMessageForFetchError(err)))
      .finally(() => setLoading(false));
  }, [account_id, serverUrl, filterProgramId, filterOrganizerId, filterFreeText]);

  // Load in embedded mode too (QR Check-In): AOP membership falls back to program_id → Program.name.
  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  useEffect(() => {
    let cancelled = false;
    fetchCurrentAopProgramNames({ serverUrl, account_id })
      .then((names) => {
        if (!cancelled) {
          setAopProgramNames(names);
          setAopProgramsLoaded(true);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setBannerError(getMessageForFetchError(err, "Failed to load Current AOP programs."));
          setAopProgramsLoaded(true); // leave names null → conservative AOP (full record flow)
        }
      });
    return () => {
      cancelled = true;
    };
  }, [serverUrl, account_id]);

  useEffect(() => {
    if (embedded) return;
    loadProgramMembers(filterProgramId);
    if (!filterProgramId) setFilterOrganizerId("");
  }, [embedded, filterProgramId, loadProgramMembers]);

  useEffect(() => {
    if (embedded) return;
    if (step === 1) searchOpenEvents();
  }, [embedded, step, filterProgramId, filterOrganizerId, filterFreeText, searchOpenEvents]);

  const loadEventParticipants = useCallback(() => {
    if (!selectedEvent || !selectedEvent._id) {
      setEventParticipants([]);
      return;
    }
    setEventParticipantsLoading(true);
    apiFetch(serverUrl + "/getEventParticipants", {
      method: "POST",
      body: JSON.stringify({ account_id, event_id: selectedEvent._id }),
    })
      .then((resp) => resp.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setEventParticipants(list);
        setBulkSelectedIds(new Set());
      })
      .catch(() => {
        setEventParticipants([]);
        setBulkSelectedIds(new Set());
      })
      .finally(() => setEventParticipantsLoading(false));
  }, [account_id, serverUrl, selectedEvent]);

  const markCloseRecordAttendance = useCallback(
    async (participantId, kind) => {
      const id = participantId != null ? String(participantId) : "";
      if (!id || !account_id) {
        throw new Error("Participant is required.");
      }
      const path = kind === "noshow" ? "/markParticipantNoShow" : "/markParticipantAttended";
      const res = await apiFetch(`${serverUrl}${path}`, {
        method: "POST",
        body: JSON.stringify({ account_id, participant_id: id }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(
          errData.error || (kind === "noshow" ? "Failed to mark no-show." : "Failed to mark attended.")
        );
      }
    },
    [account_id, serverUrl]
  );

  const runCloseRecordAttendanceForIds = useCallback(
    async (ids, kind) => {
      const idList = (ids || []).map((x) => String(x).trim()).filter(Boolean);
      if (idList.length === 0) return;
      setCloseRecordBulkBusy(kind === "noshow" ? "noshow" : "checkin");
      setBannerError("");
      try {
        for (const id of idList) {
          await markCloseRecordAttendance(id, kind);
        }
        await loadEventParticipants();
      } catch (e) {
        setBannerError(getMessageForFetchError(e));
        await loadEventParticipants();
      } finally {
        setCloseRecordBulkBusy(null);
      }
    },
    [markCloseRecordAttendance, loadEventParticipants]
  );

  const handleCloseRecordCheckIn = useCallback(
    async (participantId) => {
      const id = participantId != null ? String(participantId) : "";
      if (!id || !account_id) return;
      setCloseRecordActionBusyId(id);
      setBannerError("");
      try {
        await markCloseRecordAttendance(id, "checkin");
        await loadEventParticipants();
      } catch (e) {
        setBannerError(getMessageForFetchError(e));
      } finally {
        setCloseRecordActionBusyId(null);
      }
    },
    [account_id, markCloseRecordAttendance, loadEventParticipants]
  );

  const handleCloseRecordNoShow = useCallback(
    async (participantId) => {
      const id = participantId != null ? String(participantId) : "";
      if (!id || !account_id) return;
      setCloseRecordActionBusyId(id);
      setBannerError("");
      try {
        await markCloseRecordAttendance(id, "noshow");
        await loadEventParticipants();
      } catch (e) {
        setBannerError(getMessageForFetchError(e));
      } finally {
        setCloseRecordActionBusyId(null);
      }
    },
    [account_id, markCloseRecordAttendance, loadEventParticipants]
  );

  useEffect(() => {
    if (step === 2 && selectedEvent) {
      setWizardStep(0);
      setBulkSelectedIds(new Set());
      setCheckInSearch("");
      setCheckInSortConfig({ key: null, direction: "asc" });
      setRegTypeFilter("");
      setGenderFilter("");
      setAttendanceMetricFilter("");
      lastAutoFillEventIdRef.current = null;
      loadEventParticipants();
      // Default duration (hours) from event end - start; user can edit
      const start = selectedEvent.start_datetime ? new Date(selectedEvent.start_datetime) : null;
      const end = selectedEvent.end_datetime ? new Date(selectedEvent.end_datetime) : null;
      if (start && end && end > start) {
        const hours = (end - start) / (1000 * 60 * 60);
        setDurationHours(String(Math.round(hours * 100) / 100));
      } else {
        setDurationHours("");
      }
    }
  }, [step, selectedEvent, loadEventParticipants]);

  const programById = Object.fromEntries((programs || []).map((g) => [g._id, g]));
  const memberById = Object.fromEntries((programMembers || []).map((m) => [m._id, m]));

  const getEventProgramName = useCallback(
    (ev) => {
      if (!ev) return "";
      if (ev.program_name != null && String(ev.program_name).trim()) return String(ev.program_name).trim();
      const pid = ev.program_id != null ? String(ev.program_id) : "";
      if (pid && programById[pid]) return (programById[pid].name || "").trim();
      return "";
    },
    [programById]
  );

  const isEventInCurrentAop = useCallback(
    (ev) => {
      if (!ev) return false;
      return isEventProgramInCurrentAopForClose({
        programName: getEventProgramName(ev),
        programId: ev.program_id,
        aopProgramNames,
        programsResolved: programsLoaded,
      });
    },
    [getEventProgramName, aopProgramNames, programsLoaded]
  );

  const selectedEventInAop = isEventInCurrentAop(selectedEvent);
  const closePathReady = aopProgramsLoaded && programsLoaded;

  // If membership resolves to non-AOP while on Record & Retrospective, drop back to attendance review
  useEffect(() => {
    if (!selectedEvent) return;
    if (closePathReady && !selectedEventInAop && wizardStep === 1) {
      setWizardStep(0);
    }
  }, [selectedEvent, closePathReady, selectedEventInAop, wizardStep]);

  const slotParticipants = useMemo(
    () => (Array.isArray(eventParticipants) ? eventParticipants : []),
    [eventParticipants]
  );
  const checkedInFromRoster = slotParticipants.filter((p) => {
    const a = (p.attendance || "").trim();
    return a === "Attended" || a === "Walk-In";
  }).length;
  const checkedInTotal = checkedInFromRoster;

  const checkedInGenderCounts = useMemo(() => {
    let male = 0;
    let female = 0;
    let another = 0;
    let unspecified = 0;
    for (const p of slotParticipants) {
      const a = (p.attendance || "").trim();
      if (a !== "Attended" && a !== "Walk-In") continue;
      const b = getParticipantGenderSummaryBucket(p?.gender);
      if (b === "male") male += 1;
      else if (b === "female") female += 1;
      else if (b === "another") another += 1;
      else unspecified += 1;
    }
    return { male, female, another, unspecified };
  }, [slotParticipants]);

  /** Same “pending” bucket as participantFlowMetrics (Registered + other non-terminal statuses). */
  const matchesPendingAttendanceBucket = useCallback((p) => {
    const a = (p.attendance || "").trim();
    if (a === "Attended" || a === "Walk-In") return false;
    if (a === "No-Show") return false;
    if (a === "Registered") return true;
    if (a === "Waitlisted" || a === "Cancelled") return false;
    return true;
  }, []);

  // Auto-populate gender-based participation counts from checked-in roster rows (Attended + Walk-In).
  // Runs once per event so manual edits are preserved.
  useEffect(() => {
    if (step !== 2) return;
    if (wizardStep !== 1) return;
    if (!selectedEvent?._id) return;
    if (eventParticipantsLoading) return;
    const eventId = String(selectedEvent._id);
    if (lastAutoFillEventIdRef.current === eventId) return;

    const atRaw = selectedEvent?.audience_type ? String(selectedEvent.audience_type).trim() : "";
    const at = ["Youth Only", "Adult Only", "All Ages"].includes(atRaw) ? atRaw : "Youth Only";

    if (at === "Youth Only") {
      setGirlsParticipated(checkedInGenderCounts.female);
      setBoysParticipated(checkedInGenderCounts.male);
      setAnotherParticipated(checkedInGenderCounts.another);
      setWomenParticipated(0);
      setMenParticipated(0);
    } else if (at === "Adult Only") {
      setWomenParticipated(checkedInGenderCounts.female);
      setMenParticipated(checkedInGenderCounts.male);
      setAnotherParticipated(checkedInGenderCounts.another);
      setGirlsParticipated(0);
      setBoysParticipated(0);
    } else if (at === "All Ages") {
      setAnotherParticipated(checkedInGenderCounts.another);
    }

    lastAutoFillEventIdRef.current = eventId;
  }, [step, wizardStep, selectedEvent, eventParticipantsLoading, checkedInGenderCounts]);

  const slotParticipantsForTable = useMemo(() => {
    let rows = slotParticipants;
    if (regTypeFilter) {
      rows = rows.filter((p) => {
        const rt = (getParticipantRegTypeCell(p, selectedEvent) || "").trim();
        if (regTypeFilter === "Registered") return rt === "Registered" || rt === "";
        if (regTypeFilter === "Walk-In") return rt === "Walk-In";
        return true;
      });
    }
    if (attendanceMetricFilter) {
      rows = rows.filter((p) => {
        const a = (p.attendance || "").trim();
        switch (attendanceMetricFilter) {
          case "attended_checked_in":
            return a === "Attended" || a === "Walk-In";
          case "pending":
            return matchesPendingAttendanceBucket(p);
          case "no_show":
            return a === "No-Show";
          case "waitlisted":
            return a === "Waitlisted";
          case "cancelled":
            return a === "Cancelled";
          default:
            return true;
        }
      });
    }
    if (genderFilter) {
      rows = rows.filter((p) => getParticipantGenderSummaryBucket(p?.gender) === genderFilter);
    }
    return rows;
  }, [
    slotParticipants,
    regTypeFilter,
    genderFilter,
    attendanceMetricFilter,
    selectedEvent,
    matchesPendingAttendanceBucket,
  ]);

  const participantFlowMetrics = useMemo(() => {
    const list = Array.isArray(eventParticipants) ? eventParticipants : [];
    const totalAll = list.length;
    let waitlisted = 0;
    let cancelled = 0;
    for (const p of list) {
      const a = (p.attendance || "").trim();
      if (a === "Waitlisted") waitlisted += 1;
      else if (a === "Cancelled") cancelled += 1;
    }
    let attended = 0;
    let pending = 0;
    let noShow = 0;
    for (const p of slotParticipants) {
      const a = (p.attendance || "").trim();
      if (a === "Attended" || a === "Walk-In") attended += 1;
      else if (a === "No-Show") noShow += 1;
      else if (a === "Registered") pending += 1;
      else if (a === "Waitlisted" || a === "Cancelled") {
        /* counted in waitlisted / cancelled above */
      } else pending += 1;
    }
    let regTypeRegistered = 0;
    let regTypeWalkIn = 0;
    for (const p of slotParticipants) {
      const rt = (getParticipantRegTypeCell(p, selectedEvent) || "").trim();
      if (rt === "Walk-In") regTypeWalkIn += 1;
      else regTypeRegistered += 1;
    }
    let genderMale = 0;
    let genderFemale = 0;
    let genderAnother = 0;
    let genderUnspecified = 0;
    for (const p of slotParticipants) {
      const b = getParticipantGenderSummaryBucket(p?.gender);
      if (b === "male") genderMale += 1;
      else if (b === "female") genderFemale += 1;
      else if (b === "another") genderAnother += 1;
      else genderUnspecified += 1;
    }
    return {
      totalAll,
      waitlisted,
      cancelled,
      attended,
      pending,
      noShow,
      regTypeRegistered,
      regTypeWalkIn,
      genderMale,
      genderFemale,
      genderAnother,
      genderUnspecified,
    };
  }, [eventParticipants, slotParticipants, selectedEvent]);

  const closeCheckInTableRows = useMemo(() => {
    const q = (checkInSearch || "").trim().toLowerCase();
    const rows = slotParticipantsForTable;
    const filtered = q
      ? rows.filter((p) => {
          const name = `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase();
          const email = (p.email || "").toLowerCase();
          const phone = (p.phone || "").toLowerCase();
          const gender = (p.gender != null ? String(p.gender) : "").toLowerCase();
          const regType = getParticipantRegTypeCell(p, selectedEvent).toLowerCase();
          const att = (p.attendance || "").trim().toLowerCase();
          const serverLab = getServerAttendanceSortLabel(p).toLowerCase();
          if (name.includes(q) || email.includes(q) || phone.includes(q)) return true;
          if (gender.includes(q)) return true;
          if (regType.includes(q)) return true;
          if (att.includes(q)) return true;
          if (serverLab.includes(q)) return true;
          return false;
        })
      : rows;

    const normalized = filtered.map((p) => {
      const base = buildParticipantSortRow(p);
      const genderLabel = getParticipantGenderTableLabel(p.gender);
      return {
        ...base,
        reg_type: getParticipantRegTypeCell(p, selectedEvent),
        gender_label: genderLabel,
        attendance_label: getServerAttendanceSortLabel(p),
        server_att_key: getServerAttendancePillKey(p),
        bulk_selected: bulkSelectedIds.has(String(p._id)) ? 1 : 0,
      };
    });
    const keyTypes = {
      participant_name: "string",
      registered_at: "date",
      reg_type: "string",
      gender_label: "string",
      attendance_label: "string",
      phone: "string",
      bulk_selected: "number",
    };
    const sortConfig =
      checkInSortConfig?.key === "email"
        ? { ...checkInSortConfig, key: "participant_name" }
        : checkInSortConfig;
    if (!sortConfig?.key) {
      return sortRowsByConfig(normalized, { key: "participant_name", direction: "asc" }, keyTypes);
    }
    return sortRowsByConfig(normalized, sortConfig, keyTypes);
  }, [slotParticipantsForTable, checkInSearch, checkInSortConfig, selectedEvent, bulkSelectedIds]);

  const handleCloseCheckInSort = useCallback((key) => {
    setCheckInSortConfig((prev) => {
      const prevKey = prev?.key || null;
      const prevDir = prev?.direction === "desc" ? "desc" : "asc";
      const direction = prevKey === key && prevDir === "asc" ? "desc" : "asc";
      return { key, direction };
    });
  }, []);

  const clearParticipantViewFilters = useCallback(() => {
    setRegTypeFilter("");
    setGenderFilter("");
    setAttendanceMetricFilter("");
  }, []);

  const applyAttendanceMetricFilter = useCallback((key) => {
    const next = key != null ? String(key) : "";
    setAttendanceMetricFilter(next);
    if (next) {
      setRegTypeFilter("");
      setGenderFilter("");
    }
  }, []);

  const handleAttendanceMetricPillClick = useCallback((key) => {
    setAttendanceMetricFilter((prev) => (prev === key ? "" : key));
    setRegTypeFilter("");
    setGenderFilter("");
  }, []);

  const handleRegTypeSummaryPillClick = useCallback((id) => {
    setRegTypeFilter((prev) => (prev === id ? "" : id));
    setAttendanceMetricFilter("");
    setGenderFilter("");
  }, []);

  const handleGenderSummaryPillClick = useCallback((id) => {
    setGenderFilter((prev) => (prev === id ? "" : id));
    setAttendanceMetricFilter("");
    setRegTypeFilter("");
  }, []);

  function toggleRowBulkSelect(participantId) {
    const id = participantId != null ? String(participantId) : "";
    if (!id) return;
    setBulkSelectedIds((prev) => {
      const next = new Set([...prev].map((x) => String(x)));
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllVisibleBulkSelect(checked) {
    const ids = closeCheckInTableRows.map((r) => String(r._id));
    setBulkSelectedIds((prev) => {
      const next = new Set([...prev].map((x) => String(x)));
      if (checked) {
        ids.forEach((id) => next.add(id));
      } else {
        ids.forEach((id) => next.delete(id));
      }
      return next;
    });
  }

  const bulkSelectedCheckInIds = useMemo(() => {
    const selected = [...bulkSelectedIds].map((x) => String(x));
    return selected.filter((id) => {
      const p = slotParticipants.find((row) => String(row._id) === id);
      return p && canCloseRecordCheckIn(p);
    });
  }, [bulkSelectedIds, slotParticipants]);

  const bulkSelectedNoShowIds = useMemo(() => {
    const selected = [...bulkSelectedIds].map((x) => String(x));
    return selected.filter((id) => {
      const p = slotParticipants.find((row) => String(row._id) === id);
      return p && canCloseRecordNoShow(p);
    });
  }, [bulkSelectedIds, slotParticipants]);

  function handleRecordClick(ev) {
    setSelectedEvent(ev);
    setWizardStep(0);
    setGirlsParticipated(0);
    setBoysParticipated(0);
    setWomenParticipated(0);
    setMenParticipated(0);
    setAnotherParticipated(0);
    setDurationHours("");
    setImpactHoursEntered("");
    setWhatWentWell("");
    setWhatCouldImprove("");
    setBulkSelectedIds(new Set());
    setNoShowDialog({ open: false, participantIds: [], message: "" });
    setCheckInSearch("");
    setCheckInSortConfig({ key: null, direction: "asc" });
    setRegTypeFilter("");
    setGenderFilter("");
    setAttendanceMetricFilter("");
    setCloseRecordActionBusyId(null);
    setCloseRecordBulkBusy(null);
    lastAutoFillEventIdRef.current = null;
    setStep(2);
  }

  // Embedded (Event Check-In): keep wizard locked to the page's selected event.
  useEffect(() => {
    if (!embedded) return;
    if (!closePathReady) return;
    const nextId = preselectedEvent?._id != null ? String(preselectedEvent._id) : "";
    if (!nextId) {
      setSelectedEvent(null);
      setEventParticipants([]);
      setWizardStep(0);
      setBulkSelectedIds(new Set());
      setStep(2);
      return;
    }
    const currentId = selectedEvent?._id != null ? String(selectedEvent._id) : "";
    if (currentId === nextId) return;
    handleRecordClick(preselectedEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when host event id changes
  }, [embedded, closePathReady, preselectedEvent?._id]);

  // Deep-link from Event Details modal: /Events?tab=closeRecord&eventId=...
  useEffect(() => {
    if (embedded) return;
    const eventId = (searchParams.get("eventId") || "").trim();
    if (!eventId) return;
    const wizardStepParam = (searchParams.get("wizardStep") || "").trim();
    if (loading) return;
    // Wait for AOP + programs so program_id resolves before choosing Record vs direct Close
    if (!closePathReady) return;
    if (!Array.isArray(openEvents) || openEvents.length === 0) return;
    const found = openEvents.find((e) => String(e?._id) === String(eventId));
    if (!found) return;
    handleRecordClick(found);
    // Non-AOP programs skip Record & Retrospective — ignore wizardStep=1 deep-link
    if (wizardStepParam === "1" && isEventInCurrentAop(found)) setWizardStep(1);
    const next = new URLSearchParams(searchParams);
    next.delete("eventId");
    next.delete("wizardStep");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally one-time consumption per URL
  }, [embedded, openEvents, loading, closePathReady]);

  function handleBack() {
    if (embedded) {
      setWizardStep(0);
      if (preselectedEvent?._id) {
        handleRecordClick(preselectedEvent);
      }
      return;
    }
    setStep(1);
    setSelectedEvent(null);
    setEventParticipants([]);
    setWizardStep(0);
    setBulkSelectedIds(new Set());
    setNoShowDialog({ open: false, participantIds: [], message: "" });
    setCloseRecordBulkBusy(null);
  }

  function finishCloseSuccess(data) {
    if (data?.newEventId) {
      setNewEventIdFromSubmit(data.newEventId);
    } else {
      openAlert(
        data?.message || (selectedEventInAop ? "Event closed and recorded successfully." : "Event closed successfully."),
        selectedEventInAop ? "Record & Close Event" : "Close Event"
      );
    }
    if (embedded) {
      setSelectedEvent(null);
      setEventParticipants([]);
      setWizardStep(0);
      setStep(2);
      if (typeof onClosed === "function") {
        onClosed(data && data.newEventId ? { newEventId: data.newEventId } : {});
      }
      return;
    }
    setStep(1);
    setSelectedEvent(null);
    searchOpenEvents();
  }

  function assertNoPendingAttendance() {
    const list = Array.isArray(eventParticipants) ? eventParticipants : [];
    const pendingCount = list.filter((p) => getServerAttendancePillKey(p) === "pending").length;
    if (pendingCount > 0) {
      const eventName = (selectedEvent?.name && String(selectedEvent.name).trim()) || "";
      const eventDateTime =
        selectedEvent?.start_datetime
          ? `${dayjs(selectedEvent.start_datetime).format("MM/DD/YYYY h:mm A")}${
              selectedEvent.end_datetime ? ` – ${dayjs(selectedEvent.end_datetime).format("h:mm A")}` : ""
            }`
          : "";
      openAlert(
        <PendingAttendanceModalMessage
          pendingCount={pendingCount}
          eventName={eventName}
          eventDateTime={eventDateTime}
          continueActionLabel={selectedEventInAop ? "Next" : "Close Event"}
        />,
        "Pending attendance"
      );
      return false;
    }
    return true;
  }

  function handleDirectClose() {
    if (!selectedEvent?._id) return;
    if (!assertNoPendingAttendance()) return;
    setSubmitting(true);
    setBannerError("");
    apiFetch(serverUrl + "/closeEvent", {
      method: "POST",
      body: JSON.stringify({ account_id, _id: selectedEvent._id }),
    })
      .then((r) => {
        if (!r.ok) return r.text().then((t) => Promise.reject(new Error(t)));
        return r.json();
      })
      .then((data) => {
        finishCloseSuccess(data);
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)))
      .finally(() => setSubmitting(false));
  }

  function handleStep1Next() {
    if (!assertNoPendingAttendance()) return;
    if (!selectedEventInAop) {
      handleDirectClose();
      return;
    }
    setWizardStep(1);
  }

  function openAlert(message, title = "Message") {
    setAlertDialog({ open: true, title, message: message || "" });
  }

  function requestBulkCheckIn() {
    if (bulkSelectedCheckInIds.length === 0) {
      openAlert(
        "Check in applies to Pending (Registered) and No-show rows. Select at least one eligible participant.",
        "Check in not available"
      );
      return;
    }
    void runCloseRecordAttendanceForIds(bulkSelectedCheckInIds, "checkin");
  }

  function requestBulkNoShow() {
    const pendingIds = bulkSelectedNoShowIds;
    if (pendingIds.length === 0) {
      openAlert(
        "No-show applies only to Pending (Registered) rows. Select at least one pending participant.",
        "No-show not available"
      );
      return;
    }
    const skipped = bulkSelectedIds.size - pendingIds.length;
    const msg =
      pendingIds.length === 1
        ? "Confirm no-show for this participant? They held a registration spot but did not attend."
        : `Confirm no-show for ${pendingIds.length} participant(s)? They held a registration spot but did not attend.${
            skipped > 0 ? ` ${skipped} selected row(s) are not pending and will be skipped.` : ""
          }`;
    setNoShowDialog({ open: true, participantIds: pendingIds, message: msg });
  }

  const audienceType = selectedEvent && ["Youth Only", "Adult Only", "All Ages"].includes(selectedEvent.audience_type)
    ? selectedEvent.audience_type
    : "Youth Only";
  const totalFromMetrics = (Number(girlsParticipated) || 0) + (Number(boysParticipated) || 0) + (Number(womenParticipated) || 0) + (Number(menParticipated) || 0) + (Number(anotherParticipated) || 0);
  const durationHoursNum = durationHours !== "" && !Number.isNaN(Number(durationHours)) ? Number(durationHours) : null;
  const computedImpactHours = durationHoursNum != null && totalFromMetrics >= 0
    ? Math.round(durationHoursNum * totalFromMetrics * 100) / 100
    : null;
  const impactHoursEnteredNum =
    impactHoursEntered !== "" && !Number.isNaN(Number(impactHoursEntered))
      ? Number(impactHoursEntered)
      : 0;
  const kidHoursTotalPreview =
    computedImpactHours != null
      ? Math.round((computedImpactHours + impactHoursEnteredNum) * 100) / 100
      : impactHoursEntered !== "" && !Number.isNaN(Number(impactHoursEntered))
        ? Math.round(impactHoursEnteredNum * 100) / 100
        : null;

  function handleSubmit() {
    const durationVal = durationHours !== "" && !Number.isNaN(Number(durationHours)) ? Number(durationHours) : null;
    if (durationVal == null || durationVal < 0) {
      openAlert("Duration Hours is required. Please enter a value (e.g. 1.5).", "Validation");
      return;
    }
    if (
      impactHoursEntered !== "" &&
      (Number.isNaN(Number(impactHoursEntered)) || Number(impactHoursEntered) < 0)
    ) {
      openAlert("Kid hours (entered) must be a number greater than or equal to 0.", "Validation");
      return;
    }
    const girls = Number(girlsParticipated) || 0;
    const boys = Number(boysParticipated) || 0;
    const women = Number(womenParticipated) || 0;
    const men = Number(menParticipated) || 0;
    const another = Number(anotherParticipated) || 0;
    const sumMetrics = girls + boys + women + men + another;
    // When no roster check-ins, allow manual Girls/Boys/etc. so Kid hours (record) can be Duration × those counts.
    if (checkedInTotal > 0 && sumMetrics !== checkedInTotal) {
      openAlert(
        "Attendance metrics sum must equal Total Participated.\n\n" +
        (audienceType === "All Ages"
          ? `Girls (${girls}) + Boys (${boys}) + Women (${women}) + Men (${men}) + Another (${another}) = ${sumMetrics}\n`
          : audienceType === "Youth Only"
            ? `Girls (${girls}) + Boys (${boys}) + Another (${another}) = ${sumMetrics}\n`
            : `Women (${women}) + Men (${men}) + Another (${another}) = ${sumMetrics}\n`) +
        `Total Participated (roster Attended + Walk-In) = ${checkedInTotal}\n\n` +
        "Please adjust the participation counts so the sum equals Total Participated.",
        "Validation"
      );
      return;
    }
    const participant_attendances = slotParticipants
      .map((p) => {
        const attendance = getAttendanceForClosePayload(p);
        if (attendance == null) return null;
        return { _id: p._id, attendance };
      })
      .filter(Boolean);
    const enteredForApi =
      impactHoursEntered !== "" && !Number.isNaN(Number(impactHoursEntered))
        ? Number(impactHoursEntered)
        : 0;
    setSubmitting(true);
    setBannerError("");
    apiFetch(serverUrl + "/closeEventWithRecord", {
      method: "POST",
      body: JSON.stringify({
        account_id,
        _id: selectedEvent._id,
        girls_participated: girls,
        boys_participated: boys,
        women_participated: women,
        men_participated: men,
        another_participated: another,
        duration_hours: Math.round(durationVal * 100) / 100,
        impact_hours_entered: enteredForApi,
        what_went_well: whatWentWell.trim() || undefined,
        what_could_improve: whatCouldImprove.trim() || undefined,
        participant_attendances,
      }),
    })
      .then((r) => {
        if (!r.ok) return r.text().then((t) => Promise.reject(new Error(t)));
        return r.json();
      })
      .then((data) => {
        finishCloseSuccess(data);
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)))
      .finally(() => setSubmitting(false));
  }

  return (
    <>
    <div className={`close-event-record${embedded ? " close-event-record--embedded" : " page-content"}`}>
        <ErrorBanner message={bannerError} onDismiss={() => setBannerError("")} />
        <ConfirmationModal
          open={alertDialog.open}
          title={alertDialog.title || "Message"}
          message={alertDialog.message}
          mode="alert"
          onConfirm={() => setAlertDialog({ open: false, title: "Message", message: "" })}
          onCancel={() => setAlertDialog({ open: false, title: "Message", message: "" })}
        />
        <ConfirmationModal
          open={noShowDialog.open}
          title="No-show"
          message={noShowDialog.message}
          mode="confirm"
          confirmLabel="No-show"
          cancelLabel="Cancel"
          confirmDisabled={Boolean(closeRecordBulkBusy)}
          onConfirm={() => {
            const ids = noShowDialog.participantIds;
            setNoShowDialog({ open: false, participantIds: [], message: "" });
            if (ids.length > 0) void runCloseRecordAttendanceForIds(ids, "noshow");
          }}
          onCancel={() => setNoShowDialog({ open: false, participantIds: [], message: "" })}
        />

        {newEventIdFromSubmit && (
          <div className="panel panel-success panel-row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
            <span>Event closed. A new occurrence was created.</span>
            <button type="button" className="btn btn-primary-outline" onClick={() => { navigate(buildEventsManagementPath({ tab: EVENT_MGMT_TAB_EVENTS })); setNewEventIdFromSubmit(null); }}>
              View new event
            </button>
          </div>
        )}

        {embedded && !selectedEvent && (
          <p className="panel-hint" style={{ marginTop: "8px" }}>
            Choose an event above to review attendance and close it here.
          </p>
        )}

        {!embedded && step === 1 && (
        <>
          <div className="toolbar">
            <input
              type="text"
              value={filterFreeText}
              onChange={(e) => setFilterFreeText(e.target.value)}
              placeholder="Search name, description, location, program, organizer, status…"
              className="input input-search-wide"
            />
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setFilterFreeText("")}>
              Clear
            </button>
            <span className="toolbar-divider" />
            <span className="toolbar-label">Program</span>
            <select value={filterProgramId} onChange={(e) => setFilterProgramId(e.target.value)} className="input" style={{ minWidth: "140px" }}>
              <option value="">— All —</option>
              {(programs || []).map((g) => (
                <option key={g._id} value={g._id}>{g.name}</option>
              ))}
            </select>
            <span className="toolbar-label">Hosted By</span>
            <select value={filterOrganizerId} onChange={(e) => setFilterOrganizerId(e.target.value)} className="input" style={{ minWidth: "140px" }}>
              <option value="">— All —</option>
              {(programMembers || []).map((m) => (
                <option key={m._id} value={m._id}>{[m.first_name, m.last_name].filter(Boolean).join(" ")}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            {openEvents.length === 0 && !loading && (
              <p className="panel-hint">
                No open events match. Adjust filters and search, or add events from Events.
              </p>
            )}
            {openEvents.length > 0 && (
              <div className="cards-wrapper cards-wrapper--events">
                {openEvents.map((ev) => {
                  const programName =
                    ev.program_id && programById[ev.program_id] ? programById[ev.program_id].name : "";
                  const organizerName =
                    ev.organizer_name != null && ev.organizer_name !== ""
                      ? ev.organizer_name
                      : ev.event_organizer_id && memberById[ev.event_organizer_id]
                        ? [memberById[ev.event_organizer_id].first_name, memberById[ev.event_organizer_id].last_name]
                            .filter(Boolean)
                            .join(" ")
                        : "";
                  const subtitle = [
                    ev.start_datetime && dayjs(ev.start_datetime).format("MM/DD/YYYY h:mm A"),
                    ev.end_datetime && dayjs(ev.end_datetime).format("h:mm A"),
                    programName,
                    organizerName,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  const heroBgUrl = resolveEventBackgroundImageUrl(ev.background_image_url);
                  return (
                    <article
                      key={ev._id}
                      className="card EventCalendar-card EventCalendar-card--hero"
                    >
                      <div
                        className="EventCalendar-card-hero-banner"
                        style={{
                          backgroundImage: `url(${heroBgUrl})`,
                        }}
                        aria-hidden={true}
                      />
                      <div className="EventCalendar-card-body">
                        <div className="EventCalendar-card-date">
                          <IconCalendar />
                          {ev.start_datetime
                            ? dayjs(ev.start_datetime).format("dddd, MMMM D, YYYY")
                            : "—"}
                        </div>
                        <h3 className="EventCalendar-card-title">
                          <button type="button" className="btn-link" style={{ width: "100%", textAlign: "left" }} onClick={() => setDetailEventId(ev._id)} title="View event details">
                            {ev.name || "Event"}
                          </button>
                        </h3>
                        {(ev.location && ev.location.trim()) || subtitle ? (
                          <div className="EventCalendar-card-location" title={ev.location || subtitle}>
                            <IconLocation />
                            <span>{ev.location && ev.location.trim() ? ev.location.trim() : subtitle}</span>
                          </div>
                        ) : null}
                        <EventDescriptionPreview
                          description={ev.description}
                          dialogTitle={ev.name ? `${ev.name} — Description` : "Description"}
                        />
                        <div className="EventCalendar-card-actions CloseEventRecord-card-actions">
                          <KidHoursTrackingStatus tracked={isEventInCurrentAop(ev)} />
                          <button
                            type="button"
                            className="CloseEventRecord-btn-record"
                            onClick={() => handleRecordClick(ev)}
                          >
                            {isEventInCurrentAop(ev) ? "Record Kid Hours & Close" : "Close Event"}
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

        {step === 2 && selectedEvent && (
        <>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div className="toolbar" style={{ borderBottom: "1px solid #dee2e6", marginBottom: "6px" }}>
              {(selectedEventInAop
                ? ["Review Attendance", "Record & Retrospective"]
                : ["Review Attendance"]
              ).map((label, i) => (
                <span key={i} style={{ fontSize: "11px", color: wizardStep === i ? "#0d6efd" : "#6c757d", fontWeight: wizardStep === i ? 600 : 400 }}>
                  {i + 1}. {label}
                  {selectedEventInAop && i < 1 && <span style={{ marginLeft: "6px", color: "#adb5bd" }}>›</span>}
                </span>
              ))}
            </div>

            <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <div className="panel" style={{ background: "#e7f1ff", border: "1px solid #b6d4fe", fontSize: "12px" }}>
                <strong>Event:</strong>{" "}
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => setDetailEventId(selectedEvent._id)}
                  title="View event details"
                  style={{ fontSize: "inherit" }}
                >
                  {selectedEvent.name}
                </button>
                {selectedEvent.start_datetime && (
                  <span className="panel-hint-inline" style={{ marginLeft: "6px" }}>
                    {dayjs(selectedEvent.start_datetime).format("MM/DD/YYYY h:mm A")}
                    {selectedEvent.end_datetime && ` – ${dayjs(selectedEvent.end_datetime).format("h:mm A")}`}
                  </span>
                )}
                {selectedEvent.location && selectedEvent.location.trim() && (
                  <span className="panel-hint-inline" style={{ marginLeft: "6px" }}> · {selectedEvent.location.trim()}</span>
                )}
                {selectedEvent.repeat_rule && getRepeatLabel(selectedEvent.repeat_rule) !== 'Never' && (
                  <div className="panel-hint" style={{ marginTop: "6px" }}>Recurring: {getRepeatLabel(selectedEvent.repeat_rule)}. Closing will create a new occurrence.</div>
                )}
              </div>

              <div className="CloseEventRecord-metrics-wrap">
                <button
                  type="button"
                  className="CloseEventRecord-total-bar CloseEventRecord-total-bar--clickable"
                  onClick={clearParticipantViewFilters}
                  title="Clear attendance, Reg Type, and Gender filters; show all participants in the table"
                >
                  <span className="CloseEventRecord-total-label">
                    TOTAL REGISTERED: <strong>{participantFlowMetrics.totalAll}</strong>
                  </span>
                </button>
                <div className="CloseEventRecord-summary-grid">
                  <div className="panel CloseEventRecord-summary-panel">
                    <div className="panel-title CloseEventRecord-summary-title">Summary by Attendance</div>
                    <div className="CloseEventRecord-pill-row CloseEventRecord-pill-row--attendance">
                      <button
                        type="button"
                        className={`CloseEventRecord-pill CloseEventRecord-pill--clickable CloseEventRecord-pill--attended${
                          attendanceMetricFilter === "attended_checked_in" ? " CloseEventRecord-pill--active" : ""
                        }`}
                        title="Filter table: attendance Attended or Walk-In. Count is roster rows with those statuses."
                        onClick={() => handleAttendanceMetricPillClick("attended_checked_in")}
                        aria-pressed={attendanceMetricFilter === "attended_checked_in"}
                      >
                        <span className="CloseEventRecord-pill-label">Attended (Checked-In)</span>
                        <strong>{participantFlowMetrics.attended}</strong>
                      </button>
                      <button
                        type="button"
                        className={`CloseEventRecord-pill CloseEventRecord-pill--clickable CloseEventRecord-pill--pending${
                          attendanceMetricFilter === "pending" ? " CloseEventRecord-pill--active" : ""
                        }`}
                        title="Filter table: Pending bucket (matches summary count). Click again to clear."
                        onClick={() => handleAttendanceMetricPillClick("pending")}
                        aria-pressed={attendanceMetricFilter === "pending"}
                      >
                        <span className="CloseEventRecord-pill-label">Pending</span>
                        <strong>{participantFlowMetrics.pending}</strong>
                      </button>
                      <button
                        type="button"
                        className={`CloseEventRecord-pill CloseEventRecord-pill--clickable CloseEventRecord-pill--noshow${
                          attendanceMetricFilter === "no_show" ? " CloseEventRecord-pill--active" : ""
                        }`}
                        title="Filter table: attendance No-Show"
                        onClick={() => handleAttendanceMetricPillClick("no_show")}
                        aria-pressed={attendanceMetricFilter === "no_show"}
                      >
                        <span className="CloseEventRecord-pill-label">No-Show</span>
                        <strong>{participantFlowMetrics.noShow}</strong>
                      </button>
                      <button
                        type="button"
                        className={`CloseEventRecord-pill CloseEventRecord-pill--clickable CloseEventRecord-pill--waitlist${
                          attendanceMetricFilter === "waitlisted" ? " CloseEventRecord-pill--active" : ""
                        }`}
                        title="Filter table: attendance Waitlisted"
                        onClick={() => handleAttendanceMetricPillClick("waitlisted")}
                        aria-pressed={attendanceMetricFilter === "waitlisted"}
                      >
                        <span className="CloseEventRecord-pill-label">Waitlisted</span>
                        <strong>{participantFlowMetrics.waitlisted}</strong>
                      </button>
                      <button
                        type="button"
                        className={`CloseEventRecord-pill CloseEventRecord-pill--clickable CloseEventRecord-pill--cancelled${
                          attendanceMetricFilter === "cancelled" ? " CloseEventRecord-pill--active" : ""
                        }`}
                        title="Filter table: attendance Cancelled"
                        onClick={() => handleAttendanceMetricPillClick("cancelled")}
                        aria-pressed={attendanceMetricFilter === "cancelled"}
                      >
                        <span className="CloseEventRecord-pill-label">Cancelled</span>
                        <strong>{participantFlowMetrics.cancelled}</strong>
                      </button>
                    </div>
                  </div>
                  <div className="panel CloseEventRecord-summary-panel">
                    <div className="panel-title CloseEventRecord-summary-title">Summary by Reg Type</div>
                    <div className="CloseEventRecord-pill-row">
                      <button
                        type="button"
                        className={`CloseEventRecord-pill CloseEventRecord-pill--clickable CloseEventRecord-pill--pending${
                          regTypeFilter === "Registered" ? " CloseEventRecord-pill--active" : ""
                        }`}
                        title="Filter table by Reg Type: Registered"
                        onClick={() => handleRegTypeSummaryPillClick("Registered")}
                        aria-pressed={regTypeFilter === "Registered"}
                      >
                        <span className="CloseEventRecord-pill-label">Registered</span>
                        <strong>{participantFlowMetrics.regTypeRegistered}</strong>
                      </button>
                      <button
                        type="button"
                        className={`CloseEventRecord-pill CloseEventRecord-pill--clickable CloseEventRecord-pill--walkin${
                          regTypeFilter === "Walk-In" ? " CloseEventRecord-pill--active" : ""
                        }`}
                        title="Filter table by Reg Type: Walk-In"
                        onClick={() => handleRegTypeSummaryPillClick("Walk-In")}
                        aria-pressed={regTypeFilter === "Walk-In"}
                      >
                        <span className="CloseEventRecord-pill-label">Walk-In</span>
                        <strong>{participantFlowMetrics.regTypeWalkIn}</strong>
                      </button>
                    </div>
                  </div>
                  <div className="panel CloseEventRecord-summary-panel">
                    <div className="panel-title CloseEventRecord-summary-title">Summary by Gender</div>
                    <div className="CloseEventRecord-pill-row CloseEventRecord-pill-row--gender">
                      <button
                        type="button"
                        className={`CloseEventRecord-pill CloseEventRecord-pill--clickable CloseEventRecord-pill--gender-male${
                          genderFilter === "male" ? " CloseEventRecord-pill--active" : ""
                        }`}
                        title="Filter table: gender Male (stored roster value, case-insensitive)"
                        onClick={() => handleGenderSummaryPillClick("male")}
                        aria-pressed={genderFilter === "male"}
                      >
                        <span className="CloseEventRecord-pill-label">Male</span>
                        <strong>{participantFlowMetrics.genderMale}</strong>
                      </button>
                      <button
                        type="button"
                        className={`CloseEventRecord-pill CloseEventRecord-pill--clickable CloseEventRecord-pill--gender-female${
                          genderFilter === "female" ? " CloseEventRecord-pill--active" : ""
                        }`}
                        title="Filter table: gender Female (stored roster value, case-insensitive)"
                        onClick={() => handleGenderSummaryPillClick("female")}
                        aria-pressed={genderFilter === "female"}
                      >
                        <span className="CloseEventRecord-pill-label">Female</span>
                        <strong>{participantFlowMetrics.genderFemale}</strong>
                      </button>
                      <button
                        type="button"
                        className={`CloseEventRecord-pill CloseEventRecord-pill--clickable CloseEventRecord-pill--gender-another${
                          genderFilter === "another" ? " CloseEventRecord-pill--active" : ""
                        }`}
                        title="Filter table: gender Another"
                        onClick={() => handleGenderSummaryPillClick("another")}
                        aria-pressed={genderFilter === "another"}
                      >
                        <span className="CloseEventRecord-pill-label">Another</span>
                        <strong>{participantFlowMetrics.genderAnother}</strong>
                      </button>
                      {participantFlowMetrics.genderUnspecified > 0 && (
                        <button
                          type="button"
                          className={`CloseEventRecord-pill CloseEventRecord-pill--clickable CloseEventRecord-pill--waitlist${
                            genderFilter === "unspecified" ? " CloseEventRecord-pill--active" : ""
                          }`}
                          title='Filter table: legacy rows with no gender stored'
                          onClick={() => handleGenderSummaryPillClick("unspecified")}
                          aria-pressed={genderFilter === "unspecified"}
                        >
                          <span className="CloseEventRecord-pill-label">Not specified</span>
                          <strong>{participantFlowMetrics.genderUnspecified}</strong>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {wizardStep === 0 && (
                <div className="panel" style={{ minWidth: 0, marginBottom: "10px" }}>
                  <div className="panel-title">Participants</div>
                  <p className="panel-hint" style={{ margin: "0 0 12px 0" }}>
                    <strong>Attendance</strong> shows each person’s stored status on the roster. Checkboxes select rows for bulk <strong>Check in</strong> / <strong>No-show</strong> (nothing is pre-selected). Pending and No-show rows can be checked in from bulk actions or the Actions menu; No-show applies only to Pending rows. Closing the event updates attendance from this roster for applicable statuses; waitlisted and cancelled rows are left unchanged.
                  </p>
                  <div
                    className="toolbar CloseEventRecord-participants-toolbar"
                    style={{ flexWrap: "wrap", alignItems: "center", gap: "8px" }}
                  >
                    <input
                      type="text"
                      className="input input-search-wide"
                      style={{ flex: "1 1 200px", minWidth: "min(100%, 180px)", maxWidth: "100%" }}
                      placeholder="Search by name, phone, gender, reg type, or attendance…"
                      value={checkInSearch}
                      onChange={(e) => setCheckInSearch(e.target.value)}
                      aria-label="Search participants"
                    />
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCheckInSearch("")}>
                      Clear
                    </button>
                    <span className="toolbar-divider" />
                    <span className="toolbar-label">Attendance</span>
                    <select
                      value={attendanceMetricFilter}
                      onChange={(e) => applyAttendanceMetricFilter(e.target.value)}
                      className="input select input-min-130"
                      aria-label="Filter by attendance"
                    >
                      <option value="">— All —</option>
                      {CLOSE_RECORD_ATTENDANCE_FILTER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <EntityListBulkToolbar
                    selectedCount={bulkSelectedIds.size}
                    ariaLabel="Bulk attendance actions"
                    actions={[
                      {
                        id: "checkin",
                        label: `Check in (${bulkSelectedCheckInIds.length})`,
                        busyLabel: "Working…",
                        busy: closeRecordBulkBusy === "checkin",
                        className: "btn btn-primary btn-md toolbar-control",
                        disabled:
                          bulkSelectedCheckInIds.length === 0 ||
                          eventParticipantsLoading ||
                          Boolean(closeRecordActionBusyId),
                        onClick: requestBulkCheckIn,
                        title:
                          bulkSelectedCheckInIds.length === 0
                            ? "Check in applies to Pending (Registered) and No-show rows"
                            : "Check in selected pending and no-show participants",
                      },
                      {
                        id: "noshow",
                        label: `No-show (${bulkSelectedNoShowIds.length})`,
                        busyLabel: "Working…",
                        busy: closeRecordBulkBusy === "noshow",
                        className: "btn btn-secondary btn-md toolbar-control",
                        disabled:
                          bulkSelectedNoShowIds.length === 0 ||
                          eventParticipantsLoading ||
                          Boolean(closeRecordActionBusyId),
                        onClick: requestBulkNoShow,
                        title:
                          bulkSelectedNoShowIds.length === 0
                            ? "No-show applies only to Pending (Registered) rows"
                            : "No-show selected pending participants",
                      },
                    ]}
                  />
                  <div className="table-scroll" style={{ maxHeight: "min(420px, 55vh)", marginTop: "10px" }}>
                    {eventParticipantsLoading ? (
                      <p className="panel-hint" style={{ margin: "8px 0" }}>Loading…</p>
                    ) : closeCheckInTableRows.length === 0 ? (
                      <p className="panel-hint" style={{ margin: "8px 0" }}>
                        {slotParticipantsForTable.length === 0
                          ? "No participants in this event."
                          : "No rows match your search or active summary filters."}
                      </p>
                    ) : (
                      <div className="table-wrapper">
                        <table>
                          <thead>
                            <tr>
                              <th
                                className="text-center sortable"
                                style={{ width: 44 }}
                                onClick={() => handleCloseCheckInSort("bulk_selected")}
                                title="Sort by bulk selection"
                              >
                                <input
                                  type="checkbox"
                                  checked={
                                    closeCheckInTableRows.length > 0 &&
                                    closeCheckInTableRows.every((row) => bulkSelectedIds.has(String(row._id)))
                                  }
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    toggleAllVisibleBulkSelect(e.target.checked);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  title="Select all visible rows for bulk actions"
                                  aria-label="Select all visible rows for bulk actions"
                                />
                              </th>
                              <th className="sortable" onClick={() => handleCloseCheckInSort("participant_name")}>
                                Participant
                              </th>
                              <th className="sortable" onClick={() => handleCloseCheckInSort("registered_at")}>
                                Registered
                              </th>
                              <th className="sortable" onClick={() => handleCloseCheckInSort("reg_type")}>
                                Reg Type
                              </th>
                              <th className="sortable" onClick={() => handleCloseCheckInSort("gender_label")}>
                                Gender
                              </th>
                              <th className="sortable" onClick={() => handleCloseCheckInSort("attendance_label")}>
                                Attendance
                              </th>
                              <th className="sortable" onClick={() => handleCloseCheckInSort("phone")}>
                                Phone
                              </th>
                              <th className="text-center" style={{ minWidth: 160 }}>
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {closeCheckInTableRows.map((p) => {
                              const displayName = [p.first_name, p.last_name].filter(Boolean).join(" ").trim() || "—";
                              const regTypeCell = (p.reg_type != null ? String(p.reg_type) : "").trim() || "—";
                              const genderCell = p.gender_label;
                              const sk = p.server_att_key || "other";
                              const rowCanCheckIn = canCloseRecordCheckIn(p);
                              const rowCanNoShow = canCloseRecordNoShow(p);
                              const rowActionsEnabled = rowCanCheckIn || rowCanNoShow;
                              const rowActionBusy =
                                Boolean(closeRecordBulkBusy) || closeRecordActionBusyId === String(p._id);
                              return (
                                <tr key={p._id}>
                                  <td className="text-center">
                                    <input
                                      type="checkbox"
                                      checked={bulkSelectedIds.has(String(p._id))}
                                      onChange={() => toggleRowBulkSelect(p._id)}
                                      aria-label={`Bulk select: ${displayName}`}
                                    />
                                  </td>
                                  <td>{displayName}</td>
                                  <td style={{ whiteSpace: "nowrap" }}>{getParticipantRegisteredAtCell(p)}</td>
                                  <td style={{ whiteSpace: "nowrap" }}>{regTypeCell}</td>
                                  <td>{genderCell}</td>
                                  <td>
                                    {sk === "attended" && (
                                      <span className="CloseEventRecord-cellpill CloseEventRecord-cellpill--attended">Attended</span>
                                    )}
                                    {sk === "walkin" && (
                                      <span className="CloseEventRecord-cellpill CloseEventRecord-cellpill--walkin">Walk-In</span>
                                    )}
                                    {sk === "pending" && (
                                      <span className="CloseEventRecord-cellpill CloseEventRecord-cellpill--pending">Pending</span>
                                    )}
                                    {sk === "noshow" && (
                                      <span className="CloseEventRecord-cellpill CloseEventRecord-cellpill--noshow">No-Show</span>
                                    )}
                                    {sk === "waitlist" && (
                                      <span className="CloseEventRecord-cellpill CloseEventRecord-cellpill--waitlist">Waitlist</span>
                                    )}
                                    {sk === "cancelled" && (
                                      <span className="CloseEventRecord-cellpill CloseEventRecord-cellpill--cancelled">Cancelled</span>
                                    )}
                                    {sk === "other" && (
                                      <span className="panel-hint-inline">{(p.attendance || "").trim() || "—"}</span>
                                    )}
                                  </td>
                                  <td>{formatPhoneDisplay(p.phone)}</td>
                                  <td className="text-center">
                                    {rowActionsEnabled ? (
                                      <RowActionIcons aria-label={`Actions for ${displayName}`}>
                                        {rowCanCheckIn && (
                                          <button
                                            type="button"
                                            className="btn-link"
                                            style={iconBtnStyle}
                                            title="Check in"
                                            aria-label="Check in"
                                            disabled={rowActionBusy}
                                            onClick={() => handleCloseRecordCheckIn(p._id)}
                                          >
                                            <IconApprove size={18} />
                                          </button>
                                        )}
                                        {rowCanNoShow && (
                                          <button
                                            type="button"
                                            className="btn-link-muted"
                                            style={iconBtnStyle}
                                            title="No-show (held a spot, did not attend)"
                                            aria-label="No-show"
                                            disabled={rowActionBusy}
                                            onClick={() => handleCloseRecordNoShow(p._id)}
                                          >
                                            <IconCancel size={18} />
                                          </button>
                                        )}
                                      </RowActionIcons>
                                    ) : (
                                      <span className="panel-hint-inline">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedEventInAop && wizardStep === 1 && (
                <div style={{ marginBottom: "12px" }}>
                  <div className="panel-row" style={{ flexWrap: "wrap", gap: "16px" }}>
                    <div className="panel" style={{ flex: "1 1 280px", minWidth: 0 }}>
                      <div className="panel-title">Attendance Metrics</div>
                      <p className="panel-hint" style={{ marginBottom: "6px" }}>Audience: {audienceType}. Total Participated: {checkedInTotal}</p>
                      <div className="panel-row" style={{ display: "grid", gridTemplateColumns: "minmax(0, max-content) minmax(0, max-content)", gap: "8px 12px" }}>
                        {audienceType === "Youth Only" && (
                          <>
                            <label className="panel-label">Girls Participated</label>
                            <input type="text" min={0} value={girlsParticipated} onChange={(e) => setGirlsParticipated(e.target.value)} className="input input-number-no-spin" style={{ width: "100px" }} />
                            <label className="panel-label">Boys Participated</label>
                            <input type="text" min={0} value={boysParticipated} onChange={(e) => setBoysParticipated(e.target.value)} className="input input-number-no-spin" style={{ width: "100px" }} />
                            <label className="panel-label">Another Participated</label>
                            <input type="text" min={0} value={anotherParticipated} onChange={(e) => setAnotherParticipated(e.target.value)} className="input input-number-no-spin" style={{ width: "100px" }} />
                          </>
                        )}
                        {audienceType === "Adult Only" && (
                          <>
                            <label className="panel-label">Women Participated</label>
                            <input type="text" min={0} value={womenParticipated} onChange={(e) => setWomenParticipated(e.target.value)} className="input input-number-no-spin" style={{ width: "100px" }} />
                            <label className="panel-label">Men Participated</label>
                            <input type="text" min={0} value={menParticipated} onChange={(e) => setMenParticipated(e.target.value)} className="input input-number-no-spin" style={{ width: "100px" }} />
                            <label className="panel-label">Another Participated</label>
                            <input type="text" min={0} value={anotherParticipated} onChange={(e) => setAnotherParticipated(e.target.value)} className="input input-number-no-spin" style={{ width: "100px" }} />
                          </>
                        )}
                        {audienceType === "All Ages" && (
                          <>
                            <label className="panel-label">Girls Participated</label>
                            <input type="text" min={0} value={girlsParticipated} onChange={(e) => setGirlsParticipated(e.target.value)} className="input input-number-no-spin" style={{ width: "100px" }} />
                            <label className="panel-label">Boys Participated</label>
                            <input type="text" min={0} value={boysParticipated} onChange={(e) => setBoysParticipated(e.target.value)} className="input input-number-no-spin" style={{ width: "100px" }} />
                            <label className="panel-label">Women Participated</label>
                            <input type="text" min={0} value={womenParticipated} onChange={(e) => setWomenParticipated(e.target.value)} className="input input-number-no-spin" style={{ width: "100px" }} />
                            <label className="panel-label">Men Participated</label>
                            <input type="text" min={0} value={menParticipated} onChange={(e) => setMenParticipated(e.target.value)} className="input input-number-no-spin" style={{ width: "100px" }} />
                            <label className="panel-label">Another Participated</label>
                            <input type="text" min={0} value={anotherParticipated} onChange={(e) => setAnotherParticipated(e.target.value)} className="input input-number-no-spin" style={{ width: "100px" }} />
                          </>
                        )}
                        <label className="panel-label">Duration Hours <span className="panel-required-hint">*</span></label>
                        <input type="text" min={0} step="0.01" value={durationHours} required onChange={(e) => setDurationHours(e.target.value)} placeholder="e.g. 1.5" className="input input-number-no-spin" style={{ width: "120px" }} />
                        <label className="panel-label">Kid hours (record)</label>
                        <span className="panel-hint-inline">{computedImpactHours != null ? formatKidHours(computedImpactHours) : "—"} (Duration × Total Participated)</span>
                        <label className="panel-label">Kid hours (entered)</label>
                        <input
                          type="text"
                          min={0}
                          step="0.01"
                          value={impactHoursEntered}
                          placeholder="0"
                          onChange={(e) => setImpactHoursEntered(e.target.value)}
                          className="input input-number-no-spin"
                          style={{ width: "120px" }}
                        />
                        <label className="panel-label">Kid hours (total)</label>
                        <span className="panel-hint-inline">{kidHoursTotalPreview != null ? formatKidHours(kidHoursTotalPreview) : "—"}</span>
                      </div>
                      <p className="panel-hint" style={{ marginTop: "8px", marginBottom: 0 }}>
                        Record hours = Duration × participation counts. Entered hours are optional adjustments (same as Edit Event). Total is used for Operating Plan reporting.
                      </p>
                    </div>
                    <div className="panel" style={{ flex: "1 1 280px", minWidth: 0 }}>
                      <div className="panel-title">Retrospective</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <div>
                          <label className="panel-label">What went well?</label>
                          <textarea value={whatWentWell} onChange={(e) => setWhatWentWell(e.target.value)} rows={3} className="input input-full" />
                        </div>
                        <div>
                          <label className="panel-label CloseEventRecord-retro-label-nowrap">What could be improved?</label>
                          <textarea value={whatCouldImprove} onChange={(e) => setWhatCouldImprove(e.target.value)} rows={3} className="input input-full" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="panel-hint" style={{ marginTop: "10px", marginBottom: 0 }}>Click <strong>Close Event</strong> below to save and close this event.</p>
                </div>
              )}
            </div>

            <div className="toolbar" style={{ flexShrink: 0, borderTop: "1px solid #dee2e6", marginTop: "4px", paddingTop: "8px" }}>
              {selectedEventInAop && wizardStep > 0 && (
                <button type="button" className="btn btn-secondary" onClick={() => setWizardStep((s) => s - 1)}>Back</button>
              )}
              {selectedEventInAop && wizardStep < 1 && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleStep1Next()}
                  disabled={!closePathReady || (wizardStep === 0 && eventParticipantsLoading)}
                >
                  Next
                </button>
              )}
              {!selectedEventInAop && wizardStep === 0 && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleStep1Next()}
                  disabled={!closePathReady || eventParticipantsLoading || submitting}
                >
                  {submitting ? "Closing…" : "Close Event"}
                </button>
              )}
              {selectedEventInAop && wizardStep === 1 && (
                <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={!closePathReady || submitting}>{submitting ? "Closing…" : "Close Event"}</button>
              )}
              {!embedded && (
                <button type="button" className="btn btn-secondary" onClick={handleBack} disabled={submitting} style={{ marginLeft: "auto" }}>Cancel (back to events)</button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
      {detailEventId && (
        <EventDetailModal
          eventId={detailEventId}
          open={!!detailEventId}
          onClose={() => setDetailEventId(null)}
          returnTo={
            embedded
              ? buildEventCheckInPath({ eventId: detailEventId, accountId: account_id })
              : buildEventsManagementPath({
                  tab: EVENT_MGMT_TAB_CLOSE_RECORD,
                  eventId: detailEventId,
                  wizardStep: String(wizardStep),
                })
          }
          eventsFilters={null}
        />
      )}
    </>
  );
}

export default CloseEventRecord;
